import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { exportExcel, exportPDF } from "@/lib/exporters";
import { Pencil, ArrowLeft } from "lucide-react";

const drPrefix = "To ";
const crPrefix = "By ";

const fmtBalance = (n) => {
  const abs = Math.abs(n || 0);
  if (abs < 0.005) return "0.00";
  return `${formatINR(abs)} ${n >= 0 ? "Dr" : "Cr"}`;
};

function Inner() {
  const { lid } = useParams();
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeId || !lid) return;
    api.get(`/companies/${activeId}/reports/ledger/${lid}`, { params })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [activeId, lid, applied.start, applied.end, params]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    if (!filter) return data.rows;
    const f = filter.toLowerCase();
    return data.rows.filter((r) =>
      (r.voucher_no || "").toLowerCase().includes(f) ||
      (r.type || "").toLowerCase().includes(f) ||
      (r.contra_label || r.particulars || "").toLowerCase().includes(f) ||
      (r.narration || "").toLowerCase().includes(f)
    );
  }, [data, filter]);

  const totals = useMemo(() => {
    let dr = 0, cr = 0;
    rows.forEach((r) => { dr += r.debit || 0; cr += r.credit || 0; });
    return { dr, cr };
  }, [rows]);

  const meta = { company: active?.name };

  const buildExportRows = (asPDF) => rows.map((r) => {
    const prefix = r.type === "Opening Balance" ? "" : (r.debit > 0 ? drPrefix : crPrefix);
    const part = r.type === "Opening Balance" ? "Opening Balance" : (r.contra_label ? `${prefix}${r.contra_label}` : (r.particulars || ""));
    return [
      r.date, r.voucher_no, r.type, part, r.narration || "",
      asPDF ? formatINR(r.debit || 0) : (r.debit || 0),
      asPDF ? formatINR(r.credit || 0) : (r.credit || 0),
      asPDF ? fmtBalance(r.balance) : r.balance,
    ];
  });

  const onExcel = () => {
    const sheet = {
      name: (data?.ledger?.name || "Ledger").slice(0, 28),
      rows: [
        [`Ledger: ${data?.ledger?.name || ""}`],
        [`Group: ${data?.ledger?.group || ""}`],
        [`Period: ${formatRangeLabel(range)}`],
        [],
        ["Date", "Voucher", "Type", "Particulars (Contra)", "Narration", "Debit", "Credit", "Balance"],
        ...buildExportRows(false),
        [],
        ["", "", "", "Totals", "", totals.dr, totals.cr, data?.closing_balance],
      ],
    };
    exportExcel(`ledger-${data?.ledger?.name || "report"}`, [sheet]);
  };

  const onPDF = () => {
    const sections = [{
      title: `${data?.ledger?.name || ""}  —  ${data?.ledger?.group || ""}  (Closing: ${fmtBalance(data?.closing_balance || 0)})`,
      headers: ["Date", "Voucher", "Type", "Particulars", "Narration", "Debit", "Credit", "Balance"],
      rows: buildExportRows(true),
    }];
    exportPDF(`ledger-${data?.ledger?.name || "report"}`, `${data?.ledger?.name || "Ledger"} — Statement`, sections, meta);
  };

  return (
    <>
      <PageHeader
        title={data?.ledger?.name ? `Ledger Statement — ${data.ledger.name}` : "Ledger Statement"}
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/reports/all-ledgers" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="back-all-ledgers">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to All Ledgers
          </Link>
          <span className="text-xs text-gray-300">|</span>
          {data?.ledger?.group && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{data.ledger.group}</span>
          )}
        </div>

        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />

        <div className="flex gap-3 flex-wrap items-center">
          <Input
            placeholder="Search by voucher / type / contra / narration…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-md"
            data-testid="input-filter-ledger-statement"
          />
          <span className="ml-auto text-xs text-gray-500 font-mono">{rows.length} row(s)</span>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading…</div>}
        {!loading && data && (
          <section className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="ledger-statement">
            <header className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h3 className="font-semibold text-gray-900">{data.ledger?.name}</h3>
              <span className="text-xs text-gray-500 ml-auto font-mono">
                Period Debit: <span className="text-gray-900">{formatINR(totals.dr)}</span>
              </span>
              <span className="text-xs text-gray-500 font-mono">
                Period Credit: <span className="text-gray-900">{formatINR(totals.cr)}</span>
              </span>
              <span className="text-xs text-gray-500 font-mono">
                Closing: <span className={`font-semibold ${(data.closing_balance || 0) >= 0 ? "text-blue-700" : "text-emerald-700"}`}>
                  {fmtBalance(data.closing_balance)}
                </span>
              </span>
            </header>

            <table className="tx-table">
              <thead>
                <tr>
                  <th>Date</th><th>Voucher</th><th>Type</th><th>Particulars</th>
                  <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-6 text-sm text-gray-400">No transactions in this period</td></tr>
                )}
                {rows.map((r, i) => {
                  const isOpening = r.type === "Opening Balance";
                  const prefix = isOpening ? "" : (r.debit > 0 ? drPrefix : crPrefix);
                  const contra = r.contra || [];
                  const rowKey = `${r.voucher_id || "open"}-${r.date}-${r.voucher_no || i}-${i}`;
                  return (
                    <tr key={rowKey} data-testid={`ledger-row-${i}`}>
                      <td className="font-mono text-xs whitespace-nowrap">{r.date}</td>
                      <td className="font-mono whitespace-nowrap">{r.voucher_no}</td>
                      <td><span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">{r.type}</span></td>
                      <td className="max-w-md">
                        {isOpening ? (
                          <span className="italic text-gray-500">Opening Balance</span>
                        ) : contra.length === 0 ? (
                          <span className="text-gray-700">{r.particulars || r.narration || "—"}</span>
                        ) : (
                          <div className="space-y-0.5">
                            <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                              <span className="text-gray-400 font-medium">{prefix.trim()}</span>
                              {contra.map((c, idx) => (
                                <Link
                                  key={`${c.ledger_id}-${idx}`}
                                  to={`/reports/ledger/${c.ledger_id}`}
                                  className="text-blue-700 hover:text-blue-900 hover:underline font-medium"
                                  data-testid={`contra-ledger-${c.ledger_id || idx}`}
                                  title={`Drill into ${c.ledger_name}`}
                                >
                                  {c.ledger_name}{idx < contra.length - 1 ? "," : ""}
                                </Link>
                              ))}
                              {contra.length > 1 && (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="ml-1 text-[10px] px-1 rounded bg-gray-100 text-gray-500 cursor-help">i</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <div className="space-y-1">
                                        {contra.map((c, idx) => (
                                          <div key={`${c.ledger_id || "x"}-tip-${idx}`} className="flex justify-between gap-3 font-mono text-xs">
                                            <span>{c.ledger_name}</span>
                                            <span>{formatINR(c.amount)} {c.dr_cr}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {r.narration && (
                              <div className="text-[11px] text-gray-400 italic truncate" title={r.narration}>{r.narration}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="tx-num">{r.debit ? formatINR(r.debit) : ""}</td>
                      <td className="tx-num">{r.credit ? formatINR(r.credit) : ""}</td>
                      <td className="tx-num">{fmtBalance(r.balance)}</td>
                      <td className="text-right whitespace-nowrap">
                        {r.voucher_id && !isOpening && (
                          <Link
                            to={`/vouchers/edit/${r.voucher_id}`}
                            state={{ from: `/reports/ledger/${lid}` }}
                            className="text-gray-400 hover:text-blue-700 inline-block"
                            title="Edit this voucher"
                            data-testid={`btn-edit-voucher-${r.voucher_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length > 0 && (
                  <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                    <td colSpan={4} className="text-right">Totals</td>
                    <td className="tx-num">{formatINR(totals.dr)}</td>
                    <td className="tx-num">{formatINR(totals.cr)}</td>
                    <td className="tx-num">{fmtBalance(data.closing_balance)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </>
  );
}

export default function LedgerStatement() {
  return <RequireCompany><Inner /></RequireCompany>;
}
